import { useCallback, useEffect, useState } from 'react';
import {
  ArrowUpTrayIcon,
  PlayIcon,
  DocumentMagnifyingGlassIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { formsFillApi } from '../../api/formsFill';
import { empresasApi } from '../../api/empresas';

const DEFAULT_URL = 'https://www.dgii.gov.do/ofv/login.aspx';

export default function FormsFillManager() {
  const [configs, setConfigs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [schemaCampos, setSchemaCampos] = useState([]);

  const [configId, setConfigId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [startUrl, setStartUrl] = useState(DEFAULT_URL);
  const [dryRun, setDryRun] = useState(true);
  const [archivo, setArchivo] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [mappingOpen, setMappingOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [running, setRunning] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editJson, setEditJson] = useState('');
  const [empresasLista, setEmpresasLista] = useState([]);
  const [empresaImportId, setEmpresaImportId] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cfgRes, tplRes, jobsRes, schemaRes] = await Promise.all([
        formsFillApi.listConfigs(),
        formsFillApi.listTemplates(),
        formsFillApi.listJobs(),
        formsFillApi.schemaCampos(),
      ]);
      setConfigs(Array.isArray(cfgRes) ? cfgRes : cfgRes.results || []);
      setTemplates(Array.isArray(tplRes) ? tplRes : tplRes.results || []);
      setJobs(Array.isArray(jobsRes) ? jobsRes : jobsRes.results || []);
      setSchemaCampos(schemaRes?.campos || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
    empresasApi.listar().then((r) => {
      setEmpresasLista(Array.isArray(r) ? r : r.results || []);
    }).catch(() => {});
  }, [cargar]);

  useEffect(() => {
    if (configId && configs.length) {
      const c = configs.find((x) => String(x.id) === String(configId));
      if (c) {
        setStartUrl(c.start_url || DEFAULT_URL);
        setDryRun(c.dry_run_default !== false);
        setTemplateId(String(c.template));
      }
    }
  }, [configId, configs]);

  const seleccionarJob = async (id) => {
    try {
      const job = await formsFillApi.getJob(id);
      setActiveJob(job);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error al cargar job');
    }
  };

  const crearJobVacio = async () => {
    if (!templateId) {
      setError('Seleccione una plantilla');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('template', templateId);
      if (configId) fd.append('config', configId);
      fd.append('dry_run', dryRun ? 'true' : 'false');
      if (startUrl) fd.append('start_url_override', startUrl);
      const job = await formsFillApi.createJob(fd);
      setActiveJob(job);
      await cargar();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Error al crear job');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!archivo) {
      await crearJobVacio();
      return;
    }
    if (!templateId) {
      setError('Seleccione una plantilla');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      fd.append('template', templateId);
      if (configId) fd.append('config', configId);
      fd.append('dry_run', dryRun ? 'true' : 'false');
      if (startUrl) fd.append('start_url_override', startUrl);
      fd.append('column_mapping', JSON.stringify(columnMapping));
      const job = await formsFillApi.createJob(fd);
      setActiveJob(job);
      await cargar();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Error al subir');
    } finally {
      setUploading(false);
    }
  };

  const handleParse = async () => {
    if (!activeJob?.id) return;
    setParsing(true);
    setError('');
    try {
      const job = await formsFillApi.parseJob(activeJob.id);
      setActiveJob(job);
      await cargar();
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al parsear');
    } finally {
      setParsing(false);
    }
  };

  const handleEjecutar = async (recordId = null) => {
    if (!activeJob?.id) return;
    setRunning(true);
    setError('');
    try {
      await formsFillApi.ejecutarJob(activeJob.id, { recordId, dryRun });
      const job = await formsFillApi.getJob(activeJob.id);
      setActiveJob(job);
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al ejecutar');
    } finally {
      setRunning(false);
    }
  };

  const guardarConfigUrl = async () => {
    if (!configId) {
      setError('Seleccione una configuración para guardar la URL');
      return;
    }
    try {
      await formsFillApi.updateConfig(configId, { start_url: startUrl, dry_run_default: dryRun });
      await cargar();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error al guardar configuración');
    }
  };

  const abrirEditor = (rec) => {
    setEditRecord(rec);
    setEditJson(JSON.stringify(rec.datos_revisados || rec.datos_extraccion || {}, null, 2));
  };

  const importarDesdeEmpresa = async () => {
    if (!activeJob?.id || !empresaImportId) return;
    setError('');
    try {
      await formsFillApi.desdeEmpresaCliente(activeJob.id, empresaImportId);
      const job = await formsFillApi.getJob(activeJob.id);
      setActiveJob(job);
      setEmpresaImportId('');
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al importar empresa');
    }
  };

  const guardarRecord = async () => {
    if (!editRecord) return;
    try {
      const parsed = JSON.parse(editJson);
      await formsFillApi.updateRecord(editRecord.id, { datos_revisados: parsed });
      const job = await formsFillApi.getJob(activeJob.id);
      setActiveJob(job);
      setEditRecord(null);
    } catch (e) {
      setError(e.message?.includes('JSON') ? 'JSON inválido' : (e?.response?.data?.detail || 'Error al guardar'));
    }
  };

  const addMappingRow = () => {
    setColumnMapping((m) => ({ ...m, '': '' }));
  };

  const updateMapping = (oldKey, newCol, path) => {
    setColumnMapping((m) => {
      const next = { ...m };
      if (oldKey !== newCol && oldKey in next) delete next[oldKey];
      if (newCol) next[newCol] = path;
      return next;
    });
  };

  return (
    <div className="px-4 sm:px-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">FormsFill</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Carga registros (PDF, Excel, Word) y rellena formularios DGII automáticamente
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      {/* Configuración */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Cog6ToothIcon className="h-5 w-5 text-[#12284C]" />
          <h2 className="font-semibold text-gray-900">Configuración</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Configuración guardada</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
            >
              <option value="">— Manual —</option>
              {configs.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Tipo de formulario DGII
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              disabled={templates.length === 0}
            >
              <option value="">
                {templates.length === 0 ? 'Sin plantillas en el servidor' : 'Seleccionar...'}
              </option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.nombre}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              No es un archivo: define qué campos usa el bot (ej. Constitución de empresas).
            </p>
            {templates.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">
                En Railway ejecute: python manage.py migrate forms_fill && python manage.py seed_forms_fill
              </p>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">URL de inicio del formulario</label>
            <input
              type="url"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={startUrl}
              onChange={(e) => setStartUrl(e.target.value)}
              placeholder={DEFAULT_URL}
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                className="rounded border-gray-300"
              />
              Modo dry-run (rellenar sin enviar)
            </label>
            {configId && (
              <button
                type="button"
                onClick={guardarConfigUrl}
                className="text-sm text-[#12284C] hover:underline font-medium"
              >
                Guardar URL en config
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Carga */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-1">Nuevo trabajo</h2>
        <p className="text-xs text-gray-500 mb-4">
          Suba aquí su PDF de solicitud, Excel con filas o Word con los datos del cliente.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <input
              type="file"
              accept=".pdf,.xlsx,.xls,.docx"
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#12284C] file:text-white"
              onChange={(e) => setArchivo(e.target.files?.[0] || null)}
            />
          </div>
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 bg-[#12284C] text-white rounded-lg text-sm font-medium hover:bg-[#0d1e38] disabled:opacity-50"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            {uploading ? 'Procesando...' : archivo ? 'Subir y crear job' : 'Crear job vacío'}
          </button>
        </div>

        <button
          type="button"
          onClick={() => setMappingOpen(!mappingOpen)}
          className="mt-4 flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          {mappingOpen ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
          Mapeo columnas Excel (opcional)
        </button>
        {mappingOpen && (
          <div className="mt-3 space-y-2">
            {Object.entries(columnMapping).map(([col, path], i) => (
              <div key={i} className="flex gap-2">
                <input
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                  placeholder="Columna Excel"
                  value={col}
                  onChange={(e) => updateMapping(col, e.target.value, path)}
                />
                <select
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                  value={path}
                  onChange={(e) => updateMapping(col, col, e.target.value)}
                >
                  <option value="">Campo canónico...</option>
                  {schemaCampos.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
            ))}
            <button type="button" onClick={addMappingRow} className="text-sm text-[#12284C] hover:underline">
              + Añadir columna
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista jobs */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Trabajos recientes</h3>
          {loading ? (
            <p className="text-sm text-gray-400">Cargando...</p>
          ) : jobs.length === 0 ? (
            <p className="text-sm text-gray-400">Sin trabajos</p>
          ) : (
            <ul className="space-y-2 max-h-96 overflow-y-auto">
              {jobs.map((j) => (
                <li key={j.id}>
                  <button
                    type="button"
                    onClick={() => seleccionarJob(j.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm border ${
                      activeJob?.id === j.id
                        ? 'border-[#12284C] bg-indigo-50'
                        : 'border-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">#{j.id}</span>
                    <span className="text-gray-500 ml-2">{j.nombre_archivo || j.tipo_archivo}</span>
                    <span className="block text-xs text-gray-400">{j.estado}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detalle job */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          {!activeJob ? (
            <p className="text-sm text-gray-400 text-center py-12">
              Seleccione o cree un trabajo para ver registros
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Job #{activeJob.id} — {activeJob.nombre_archivo}
                  </h3>
                  <p className="text-xs text-gray-500">Estado: {activeJob.estado}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleParse}
                    disabled={parsing}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <DocumentMagnifyingGlassIcon className="h-4 w-4" />
                    {parsing ? 'Parseando...' : 'Extraer datos'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEjecutar()}
                    disabled={running || !activeJob.records?.length}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#12284C] text-white rounded-lg hover:bg-[#0d1e38] disabled:opacity-50"
                  >
                    <PlayIcon className="h-4 w-4" />
                    {running ? 'Ejecutando...' : 'Ejecutar todos'}
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-xs font-medium text-indigo-800 mb-1">
                    Importar desde Empresas Clientes
                  </label>
                  <select
                    className="w-full border border-indigo-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                    value={empresaImportId}
                    onChange={(e) => setEmpresaImportId(e.target.value)}
                  >
                    <option value="">Seleccionar empresa...</option>
                    {empresasLista.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.nombre} {e.rnc ? `(${e.rnc})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={importarDesdeEmpresa}
                  disabled={!empresaImportId}
                  className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  Añadir registro
                </button>
              </div>

              {activeJob.mensaje_error && (
                <p className="text-sm text-amber-700 mb-3">{activeJob.mensaje_error}</p>
              )}

              {!activeJob.records?.length ? (
                <p className="text-sm text-gray-400">Sin registros. Pulse &quot;Extraer datos&quot;.</p>
              ) : (
                <div className="space-y-3">
                  {activeJob.records.map((rec) => (
                    <div
                      key={rec.id}
                      className="border border-gray-200 rounded-lg p-3"
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <span className="text-sm font-medium">Registro #{rec.indice + 1}</span>
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                            rec.estado === 'completado'
                              ? 'bg-green-100 text-green-700'
                              : rec.estado === 'error'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-600'
                          }`}>
                            {rec.estado}
                          </span>
                          <p className="text-xs text-gray-500 mt-1 truncate max-w-md">
                            {rec.datos_revisados?.empresa?.nombre_comercial
                              || rec.datos_extraccion?.empresa?.nombre_comercial
                              || rec.datos_revisados?.solicitante?.nombre
                              || '—'}
                          </p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => abrirEditor(rec)}
                            className="text-xs text-[#12284C] hover:underline"
                          >
                            Editar JSON
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEjecutar(rec.id)}
                            disabled={running}
                            className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100"
                          >
                            Ejecutar
                          </button>
                        </div>
                      </div>
                      {rec.screenshot && (
                        <a
                          href={rec.screenshot}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline mt-2 inline-block"
                        >
                          Ver captura
                        </a>
                      )}
                      {rec.log_ejecucion && (
                        <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-24">
                          {rec.log_ejecucion}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal editar JSON */}
      {editRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-4 py-3 border-b font-semibold">Editar registro #{editRecord.indice + 1}</div>
            <textarea
              className="flex-1 p-4 font-mono text-xs border-0 focus:ring-0 min-h-[300px]"
              value={editJson}
              onChange={(e) => setEditJson(e.target.value)}
            />
            <div className="flex justify-end gap-2 px-4 py-3 border-t">
              <button
                type="button"
                onClick={() => setEditRecord(null)}
                className="px-4 py-2 text-sm border rounded-lg"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={guardarRecord}
                className="px-4 py-2 text-sm bg-[#12284C] text-white rounded-lg"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
